import { useDebouncedValue } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import { IS_NOT_SET_FILTER_ITEM, IS_SET_FILTER_ITEM } from '../constants';
import type {
	FilterDropdownConfigList,
	FilterItem,
	FilterOptionType,
	FilterValue,
} from '../types';
import { useFilterItems } from '../useFilterItems';
import { getSubValueAsArray, getValueAsArray } from '../utils';

interface UseFilterDropdownListProps {
	getItems: FilterDropdownConfigList['getItems'];
	getItemsById?: FilterDropdownConfigList['getItemsById'];
	value?: FilterValue;
	hasIsNotSetOption?: boolean;
	hasIsSetOption?: boolean;
	excludeItems?: string[];
	isNested?: boolean;
	filterOptionType?: FilterOptionType;
}

export function useFilterDropdownList({
	getItems,
	getItemsById,
	value,
	hasIsNotSetOption,
	hasIsSetOption,
	excludeItems,
	isNested = false,
	filterOptionType,
}: UseFilterDropdownListProps) {
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 200);
	const { error, loading, items } = useFilterItems({
		getItems,
		searchTerm: debouncedSearchTerm,
		getItemsById,
		value,
		excludeItems,
		isNested,
		filterOptionType,
	});
	const [debouncedLoading] = useDebouncedValue(loading, 300);

	const { selected, unselected } = useMemo(() => {
		const values = isNested
			? getSubValueAsArray(value)
			: getValueAsArray(value);
		const { selectedItems, unselectedItems } = items.reduce<{
			selectedItems: FilterItem[];
			unselectedItems: FilterItem[];
		}>(
			(acc, item) => {
				if (values.includes(item.value)) {
					acc.selectedItems.push(item);
				} else {
					acc.unselectedItems.push(item);
				}
				return acc;
			},
			{ selectedItems: [], unselectedItems: [] }
		);

		return {
			selected: [
				...(hasIsSetOption && value?.isSetApplied ? [IS_SET_FILTER_ITEM] : []),
				...(hasIsNotSetOption && value?.isNotSetApplied
					? [IS_NOT_SET_FILTER_ITEM]
					: []),
				...selectedItems,
			],
			unselected: [
				...(hasIsSetOption && !value?.isSetApplied ? [IS_SET_FILTER_ITEM] : []),
				...(hasIsNotSetOption && !value?.isNotSetApplied
					? [IS_NOT_SET_FILTER_ITEM]
					: []),
				...unselectedItems,
			],
		};
	}, [hasIsNotSetOption, hasIsSetOption, items, value, isNested]);

	return {
		loading: debouncedLoading,
		error,
		selected,
		unselected,
		searchTerm,
		setSearchTerm,
	};
}
